import { MessageCircle } from "lucide-react";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import type { ChatUnreadBlock as ChatUnreadBlockData } from "@ferrivia/api-contract";
import { Notification } from "../../../../components/Notification";
import { useTabNavigation } from "../../../../app/useTabNavigation";

/**
 * The one notification home renders today: unread messages from the care
 * team. A block like any other on the wire — the server decides whether it
 * exists (journey/rules.ts admits it at slot "pinned" whenever
 * unreadChatCount > 0) — but rendered through <Notification> rather than as a
 * Card, and routed above the block column by HomeScreen.
 *
 * MessageCircle is the chat tab's own icon (app/routes.ts), so the row points
 * at its destination with the same glyph the destination wears.
 *
 * No message preview and no sender beyond the team name: the thread has one
 * sender, so "who from" is a constant, and the message text itself is care
 * content that home — a glanceable, hand-the-phone-over surface — is not the
 * place for. See product/prototypes/home-notification.md.
 */
export function ChatUnreadBlock({ id, unreadCount }: ChatUnreadBlockData) {
  const { t } = useTranslation("client");
  const { goToTab } = useTabNavigation();
  const tracker = useTracker();

  const handleClick = () => {
    tracker.track("home.block_cta_clicked", { block_id: id });
    // Through the shared hook, not a bare navigate: this is the same tab
    // change the bar makes, so it has to leave the history stack in the same
    // shape (see TabNavigation) and be counted with the same event.
    goToTab("/chat", "in_app_link");
  };

  return (
    <Notification
      icon={MessageCircle}
      marked
      title={t("home.blocks.chatUnread.title", {
        count: unreadCount,
        defaultValue: "{{count}} uutta viestiä",
      })}
      // The same name the chat screen's own header uses, so the row and the
      // screen it opens introduce the team identically.
      detail={t("chat.headerName", "Ferrivian terveystiimi")}
      onClick={handleClick}
    />
  );
}
